import TableManager from "common-ui-components/components/table-manager";
import styles from "../VulnerabilitiesDashboard.module.css";
import { SeverityTagWrapper } from "../../patchColumns";
import { Link, useNavigate, useResolvedPath } from "react-router-dom";
import { useRef, useState } from "react";
import Modal from "common-ui-components/components/modal";
import { ManagedDevice } from "app/inventory-new/ScanDevices";
import { toTitleCase } from "common-ui-components/components/utils/string-utils";
import { filterConfig } from "./filterConfig";
import { isLocalhost } from "common-ui-components/components/utils/Config";
import { PublishedDateFilter, RiskScoreFilter } from "./filterComponents";
import { navigateToManualDeployment } from "../utils/vulnerabilityUtils";
import Select from "common-ui-components/components/select";
import verticalDotMenu from "common-ui-components/components/icons/vertical-dot-menu.svg";

const CVEIDWrapper = ({ record }) => {
  const { pathname: basePath } = useResolvedPath("");
  return (
    <Link
      to={{
        pathname: `${basePath}/cve-details`,
        search: `?cveId=${record.cveId}`,
      }}
      state={{ orgVulnerabilityId: record.orgVulnerabilityId }}
      className="link"
    >
      {record.cveId}
    </Link>
  );
};

const EndpointsCountWrapper = ({ record }) => {
  const { pathname: basePath } = useResolvedPath("");
  return (
    <Link
      to={{
        pathname: `${basePath}/cve-details`,
        search: `?cveId=${record.cveId}&activeTab=endpoints`,
      }}
      state={{
        orgVulnerabilityId: record.orgVulnerabilityId,
        orgPatchId: record.orgPatchId,
      }}
      className="link"
    >
      {record.count}
    </Link>
  );
};
const colorMap = {
  critical: "red",
  high: "pink",
  medium: "blue",
  low: "green",
};

const CVEListTable = ({ cveFilter = { activeFilters: {}, key: 0 } }) => {
  const [vulnerabilityId, setVulnerabilityId] = useState(null);
  const [focusedPatchId, setFocusedPatchId] = useState(null);
  const navigate = useNavigate();

  const handleClick = (record) => {
    setVulnerabilityId(record.orgVulnerabilityId);
    setFocusedPatchId(record.orgPatchId);
  };

  const columns = [
    { title: "CVE ID", accessor: "cveId", WrapperComponent: CVEIDWrapper },
    { title: "Vulnerable Software", accessor: "vulnerableSoftware" },
    { title: "CVSS Score", accessor: "cvssScore" },
    { title: "Exploit Score", accessor: "exploitScore", unsearchable: true },
    {
      title: "Published Date",
      accessor: "publishedDate",
    },
    {
      title: "Affected Endpoints",
      headerLines: 2,
      accessor: "count",
      WrapperComponent: EndpointsCountWrapper,
      setter: handleClick,
    },
    {
      title: "Severity",
      accessor: "severity",
      WrapperComponent: ({ record }) =>
        SeverityTagWrapper({ record, colorMap: colorMap }),
    },
    {
      title: "Exploit Status",
      accessor: "exploitationStatus",
      formatFunction: toTitleCase,
    },
    {
      title: "Action",
      // access
      UIComponent: ({ record }) => <Action record={record} />,
    },
  ];

  const tableActionsConfig = [
    {
      label: "Remediate",
      variant: "primary",
      disableOnNoSelection: true,
      props: {
        onClick: (selectedRows) =>
          navigateToManualDeployment(navigate, selectedRows),
      },
    },
  ];

  const publishedDateRef = useRef("");
  const riskScoreRef = useRef("");

  const filterComponents = [
    {
      heading: "Published Date",
      column: "publishedDate",
      Component: <PublishedDateFilter valueRef={publishedDateRef} />,
      valueRef: publishedDateRef,
    },
    {
      heading: "Risk Score",
      column: "riskScore",
      Component: <RiskScoreFilter valueRef={riskScoreRef} />,
      valueRef: riskScoreRef,
    },
  ];

  return (
    <>
      <TableManager
        key={cveFilter.key}
        url="/vulnerabilities"
        columns={columns}
        selectable
        actionsConfig={tableActionsConfig}
        filterConfig={{
          ...filterConfig,
          active_filters: cveFilter.activeFilters,
          activeFiltersKey: cveFilter.key,
        }}
        isRecordSelectable={(record) => record.orgPatchId}
        containerProps={{
          className: styles.table,
        }}
        headerProps={{
          style: {
            background: "none",
          },
        }}
        paginationContainerProps={{
          style: {
            background: "none",
          },
        }}
        headingText={"Total : "}
        filterComponents={filterComponents}
      />
      <Modal
        open={Boolean(vulnerabilityId)}
        setOpen={() => {
          setVulnerabilityId(null);
          setFocusedPatchId(null);
        }}
        className={styles.deviceListModal}
      >
        <DeviceTable
          vulnerabilityId={vulnerabilityId}
          patchId={focusedPatchId}
        />
      </Modal>
    </>
  );
};

const DeviceTable = ({ vulnerabilityId, patchId }) => {
  const navigate = useNavigate();

  const deviceTableColumns = [
    {
      title: "Device Name",
      accessor: "deviceName",
      WrapperComponent: ManagedDevice,
    },
    {
      title: "Vulnerable Software",
      accessor: "vulnerableSoftware",
    },
  ];

  const actionsConfig = [
    {
      label: "Remediate",
      disableOnNoSelection: true,
      variant: "primary",
      props: {
        onClick: (selectedRows) =>
          navigateToManualDeployment(
            navigate,
            [
              {
                orgPatchId: patchId,
              },
            ],
            selectedRows,
          ),
      },
    },
  ];

  return (
    <TableManager
      url={`/vulnerabilities/${vulnerabilityId}/devices`}
      headingText={"Total: "}
      columns={deviceTableColumns}
      selectable
      actionsConfig={actionsConfig}
    />
  );
};

const Action = ({ record }) => {
  const navigate = useNavigate();
  const actionsList = ["Remediate"];

  const handleOptionSelect = (option) => {
    if (option === "Remediate") {
      navigateToManualDeployment(navigate, [record]);
    }
  };

  return (
    <div>
      <Select
        active={""}
        options={actionsList}
        onOptionSelect={handleOptionSelect}
        triggerComponent={() => (
          <img
            src={verticalDotMenu}
            alt="vertical-dot-menu"
            width={18}
            height={18}
            style={{ cursor: "pointer" }}
          />
        )}
      />
    </div>
  );
};

export default CVEListTable;
